import React, { Suspense, lazy, useEffect, useState, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./Layout";
import { useAuth } from "./contexts/AuthContext";
import Spinner from "./Spinner"; // Custom Spinner for loading
import SwaggerDocs from "./components/SwaggerDocs";

// Lazy-loaded pages for better performance
const Home_Recruiter = lazy(() => import("./pages/Home_Recruiter"));
const Home_JobSeeker = lazy(() => import("./pages/Home_JobSeeker"));
const About = lazy(() => import("./pages/About"));
const Careers = lazy(() => import("./pages/Careers"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const HCMDashboard = lazy(() => import("./pages/HCMDashboard"));
const ApplicantDashboard = lazy(() => import("./pages/ApplicantDashboard"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const NoAccess = lazy(() => import("./pages/NoAccess"));

// Employee Components
const EmployeeProfile = lazy(() => import("./components/Employee/Profile/EmployeeProfile"));
const ApplyLeave = lazy(() => import("./components/Employee/ApplyLeave/ApplyLeave"));
const EmployeeDashboard = lazy(() => import("./components/Employee/Dashboard/EmployeeDashboard"));
const LeaveHistory = lazy(() => import("./components/Employee/LeaveHistory/LeaveHistory"));
const EmployeeBenefits = lazy(() => import("./components/Employee/Benefits/EmployeeBenefits"));

// HCM Dashboards
const HCMRoutes = [
  // { path: "/dashboard/admin", Component: lazy(() => import("./components/HCM/Admin/Dashboard")) },
  { path: "/dashboard/analytics", Component: lazy(() => import("./components/HCM/Analytics/Dashboard")) },
  { path: "/dashboard/applicants", Component: lazy(() => import("./components/HCM/Applicants/Dashboard")) },
  { path: "/dashboard/benefits", Component: lazy(() => import("./components/HCM/Benefits/Dashboard")) },
  { path: "/dashboard/employee-development", Component: lazy(() => import("./components/HCM/EmployeeDevelopment/Dashboard")) },
  { path: "/dashboard/employee-management", Component: lazy(() => import("./components/HCM/EmployeeManagement/Dashboard")) },
  { path: "/dashboard/enterprise", Component: lazy(() => import("./components/HCM/Enterprise/Dashboard")) },
  { path: "/dashboard/hr-services", Component: lazy(() => import("./components/HCM/HRServices/Dashboard")) },
  { path: "/dashboard/interviews", Component: lazy(() => import("./components/HCM/Interviews/Dashboard")) },
  { path: "/dashboard/jobs", Component: lazy(() => import("./components/HCM/Jobs/Dashboard")) },
  { path: "/dashboard/learning-and-development", Component: lazy(() => import("./components/HCM/LearningAndDevelopment/Dashboard")) },
  { path: "/dashboard/messaging", Component: lazy(() => import("./components/HCM/Messaging/Dashboard")) },
  { path: "/dashboard/payroll", Component: lazy(() => import("./components/HCM/Payroll/Dashboard")) },
  { path: "/dashboard/performance-management", Component: lazy(() => import("./components/HCM/PerformanceManagement/Dashboard")) },
  { path: "/dashboard/recruiter", Component: lazy(() => import("./components/HCM/Recruiter/Dashboard")) },
  { path: "/dashboard/roles", Component: lazy(() => import("./components/HCM/Roles/Dashboard")) },
  { path: "/dashboard/settings", Component: lazy(() => import("./components/HCM/Settings/Dashboard")) },
  { path: "/dashboard/time-and-absence", Component: lazy(() => import("./components/HCM/TimeAndAbsence/Dashboard")) },
  { path: "/dashboard/users", Component: lazy(() => import("./components/HCM/Users/Dashboard")) },
  { path: "/dashboard/work-management", Component: lazy(() => import("./components/HCM/WorkManagement/Dashboard")) },
  { path: "/dashboard/subscription", Component: lazy(() => import("./components/HCM/Subscription/Dashboard")) },
  { path: "/dashboard/forms", Component: lazy(() => import("./components/HCM/Forms/Dashboard")) },
  { path: "/dashboard/integrations", Component: lazy(() => import("./components/HCM/Integrations/Dashboard")) },
];

// Applicant Dashboard Routes
const ApplicantRoutes = [
  { path: "/applicant/profile", Component: lazy(() => import("./components/Applicant/Profile/Dashboard")) },
  { path: "/applicant/applications", Component: lazy(() => import("./components/Applicant/Applications/Dashboard")) },
  { path: "/applicant/interviews", Component: lazy(() => import("./components/Applicant/Interviews/Dashboard")) },
  { path: "/applicant/messaging", Component: lazy(() => import("./components/Applicant/Messaging/Dashboard")) },
  { path: "/applicant/settings", Component: lazy(() => import("./components/Applicant/Settings/Dashboard")) },
  { path: "/applicant/notifications", Component: lazy(() => import("./components/Applicant/Notifications/Dashboard")) },
  { path: "/applicant/feedback", Component: lazy(() => import("./components/Applicant/Feedback/Dashboard")) },
];

const ProtectedRoute = ({ element }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const hasRedirected = useRef(false);

  if (loading) {
    return <Spinner />;
  }

  if (!user && !hasRedirected.current) {
    hasRedirected.current = true;
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (user) {
    hasRedirected.current = false; // Reset redirection flag
    return element;
  }

  return null;
};

function App() {
  const { user, loading } = useAuth();
  const [isReady, setIsReady] = useState(false); // State to track when user is authenticated

  useEffect(() => {
    if (!loading && user !== null) {
      setIsReady(true);
      // loading(false);
    }
  }, [loading, user]);

  // if (!isReady && loading) {
  //   console.log("Loading...");
  //   return <Spinner />;
  // }

  return (
    <Layout>
      <Suspense fallback={<Spinner />}>
        <Routes>
          {/* General Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute
                element={
                  localStorage.getItem("loginType") === "employer"
                    ? <Home_Recruiter />
                    : <Home_JobSeeker />
                }
              />
            }
          />
          <Route path="/about" element={<ProtectedRoute element={<About />} />} />
          <Route path="/careers" element={<ProtectedRoute element={<Careers />} />} />
          <Route path="/contactus" element={<ProtectedRoute element={<ContactUs />} />} />
          <Route path="/contactus" element={<ProtectedRoute element={<ContactUs />} />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/docs" element={<SwaggerDocs />} />
          <Route path="/employee-profile" element={<EmployeeProfile />} />
          <Route path="/employee-dashboard" element={<EmployeeDashboard />} />
          <Route path="/employee/apply-leave" element={<ApplyLeave />} />
          <Route path="/employee/leave-history" element={<LeaveHistory />} />
          <Route path="/employee/benefits" element={<EmployeeBenefits />} />
          <Route path="*" element={<NotFound />} />

          {/* Applicant Dashboard Routes */}
          <Route path="/applicant" element={<ProtectedRoute element={<ApplicantDashboard />} />} />
          {ApplicantRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} element={<ProtectedRoute element={<Component />} />} />
          ))}

          {/* HCM Dashboard Routes */}
          <Route path="/dashboard" element={<ProtectedRoute element={<HCMDashboard />} />} />
          {HCMRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} element={<ProtectedRoute element={<Component />} />} />
          ))}

          {/* Fallback for no access */}
          <Route path="/no-access" element={<NoAccess />} />
          {/* Fallback for undefined routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

export default App;